import faqBanner from '@/../../public/storage/images/faq-banner.webp';
import Banner from '@/components/Banner';
import NoResult from '@/components/NoResult';
import { Reveal } from '@/components/Reveal';
import ShareDialog from '@/components/ShareDialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import Typography from '@/components/ui/typography';
import Band from '@/Layouts/Band';
import SectionLayout from '@/Layouts/SectionLayout';
import { Link } from '@inertiajs/react';
import { MoveRight } from 'lucide-react';

const FAQ = ({ faqs }: { faqs: { question: string; answer: string }[] }) => {
  return (
    <>
      <Banner
        image={faqBanner}
        width={1500}
        height={841}
        alt="Looking for answers"
        tagline="FAQ"
        title="Frequently Asked Questions About Samuthrika Academy"
        description="Answers to the questions we are asked most often — about courses, batches, certificates and how to enrol."
      >
        <ShareDialog
          url={window.location.href}
          title={'FAQ - ' + import.meta.env.VITE_APP_NAME}
          content="Welcome to our FAQ section, where we've curated insightful answers to common queries for a seamless experience."
        />
      </Banner>

      {/*
        One column, at prose width.

        The accordion was previously the only child of an xl:grid-cols-2 grid,
        so above 1280px it occupied the left half and the right half rendered
        empty. A question-and-answer list is reading matter anyway, and belongs
        at a measure the eye can track rather than stretched across the page.
      */}
      <Band tone="raised" pattern="lattice">
        <SectionLayout width="prose" space="lg">
          {faqs.length > 0 ? (
            <Reveal>
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, index) => (
                  <AccordionItem
                    value={`question-${index}`}
                    key={`question-${index}`}
                  >
                    <AccordionTrigger className="text-left font-semibold">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          ) : (
            <NoResult kind="Frequently Asked Questions" />
          )}
        </SectionLayout>
      </Band>

      <Band tone="ink" pattern="lattice" patternOpacity="medium">
        <SectionLayout space="lg">
          <Reveal className="flex flex-col items-start gap-5 md:items-center md:text-center">
            <Typography.Eyebrow>Still stuck?</Typography.Eyebrow>
            <Typography.Display2 className="max-w-2xl text-2xl md:text-3xl lg:text-4xl">
              Ask us directly.
            </Typography.Display2>
            <Typography.Body className="max-w-xl text-ink-foreground/75">
              If your question isn&apos;t covered here, send us a message and
              we&apos;ll call you back.
            </Typography.Body>
            <Button asChild size="lg">
              <Link href={route('contact')}>
                Contact us
                <MoveRight size={18} className="ml-2" />
              </Link>
            </Button>
          </Reveal>
        </SectionLayout>
      </Band>
    </>
  );
};

export default FAQ;
